/* Product Showcase — interactive auto-rotating carousel (custom element)
   Supports: arrows, dots, touch swipe, MOUSE DRAG with momentum + snap.
   v2.1: drag uses window-level listeners (no pointer capture) so card links stay clickable. */
if (!customElements.get('product-showcase')) {
  class ProductShowcase extends HTMLElement {
    constructor() {
      super();
      this._onMouseEnter = this.pause.bind(this);
      this._onMouseLeave = this.resume.bind(this);
      this._onFocusIn = this.pause.bind(this);
      this._onFocusOut = this.resume.bind(this);
      this._onScroll = this.onScroll.bind(this);
      this._onResize = this.measure.bind(this);
      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerMove = this.onPointerMove.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this._onDragClick = this.onDragClick.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.pshow__track');
      if (!this.track || this.track.children.length === 0) return;

      this.cards = Array.from(this.track.querySelectorAll('.pshow__card'));
      this.dotsContainer = this.querySelector('[data-dots]');
      this.autoRotate = this.dataset.autoRotate === 'true';
      this.intervalMs = parseInt(this.dataset.interval, 10) || 4000;
      this.timer = null;
      this.index = 0;
      this.visibleCount = 1;
      this.stepWidth = 0;

      /* drag state */
      this.dragging = false;
      this.dragMoved = false;
      this.dragStartX = 0;
      this.dragStartScroll = 0;
      this.dragLastX = 0;
      this.dragLastT = 0;
      this.dragVelocity = 0;
      this.momentumRaf = null;

      this.buildDots();
      this.bindArrows();
      this.bindEvents();
      this.measure();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);

      if (this.autoRotate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.start();
      }
    }

    disconnectedCallback() {
      this.stop();
      this.cancelMomentum();
      if (this.track) {
        this.track.removeEventListener('scroll', this._onScroll);
        this.track.removeEventListener('pointerdown', this._onPointerDown);
        this.track.removeEventListener('click', this._onDragClick, true);
      }
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
    }

    measure() {
      if (!this.cards.length) return;
      var first = this.cards[0];
      var second = this.cards[1] || first;
      this.stepWidth = second.getBoundingClientRect().left - first.getBoundingClientRect().left || first.getBoundingClientRect().width + 20;
      var trackRect = this.track.getBoundingClientRect();
      var visible = 1;
      this.cards.forEach(function (card) {
        var r = card.getBoundingClientRect();
        if (r.right > trackRect.left + 8 && r.left < trackRect.right - 8) visible++;
      });
      this.visibleCount = Math.max(1, Math.min(this.cards.length, visible - 1));
    }

    buildDots() {
      var self = this;
      this.dots = [];
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.cards.forEach(function (card, i) {
        var dot = document.createElement('button');
        dot.className = 'pshow__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
        dot.addEventListener('click', function () {
          self.scrollToIndex(i);
        });
        self.dotsContainer.appendChild(dot);
        self.dots.push(dot);
      });
    }

    bindArrows() {
      var self = this;
      var prev = this.querySelector('[data-dir="prev"]');
      var next = this.querySelector('[data-dir="next"]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });
    }

    bindEvents() {
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      /* mouse drag-to-scroll (touch uses native swipe).
         move/up listeners are attached to WINDOW during the drag only,
         so native clicks on card links keep working. */
      this.track.addEventListener('pointerdown', this._onPointerDown);
      /* swallow link clicks that were really drags */
      this.track.addEventListener('click', this._onDragClick, true);
    }

    /* ---------- drag ---------- */

    onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      this.cancelMomentum();
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = e.clientX;
      this.dragLastX = e.clientX;
      this.dragLastT = performance.now();
      this.dragStartScroll = this.track.scrollLeft;
      this.dragVelocity = 0;
      this.pause();
      this.track.classList.add('is-dragging');
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    onPointerMove(e) {
      if (!this.dragging) return;
      var dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > 5) this.dragMoved = true;
      this.track.scrollLeft = this.dragStartScroll - dx;
      var now = performance.now();
      var dt = now - this.dragLastT;
      if (dt > 0) {
        this.dragVelocity = (e.clientX - this.dragLastX) / dt;
      }
      this.dragLastX = e.clientX;
      this.dragLastT = now;
    }

    onPointerUp(e) {
      if (!this.dragging) return;
      this.dragging = false;
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      this.track.classList.remove('is-dragging');
      this.applyMomentum();
      /* give the browser a tick before resuming auto-rotate */
      var self = this;
      setTimeout(function () { self.resume(); }, 1200);
    }

    onDragClick(e) {
      if (this.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        this.dragMoved = false;
      }
    }

    cancelMomentum() {
      if (this.momentumRaf) {
        cancelAnimationFrame(this.momentumRaf);
        this.momentumRaf = null;
      }
    }

    applyMomentum() {
      var self = this;
      var v = this.dragVelocity * 16; /* px per frame-ish */
      if (Math.abs(v) < 1.5) {
        this.snapToNearest();
        return;
      }
      var last = performance.now();
      var decay = 0.94;
      var maxTravel = this.stepWidth * 2.5;

      function frame(now) {
        var dt = now - last;
        last = now;
        self.track.scrollLeft -= v * (dt / 16.7);
        v *= Math.pow(decay, dt / 16.7);
        var atEdge =
          self.track.scrollLeft <= 0 ||
          self.track.scrollLeft >= self.track.scrollWidth - self.track.clientWidth - 1;
        if (Math.abs(v) < 0.4 || atEdge) {
          self.momentumRaf = null;
          self.snapToNearest();
          return;
        }
        self.momentumRaf = requestAnimationFrame(frame);
      }
      void maxTravel;
      this.momentumRaf = requestAnimationFrame(frame);
    }

    snapToNearest() {
      this.updateIndex();
      this.scrollToIndex(this.index);
    }

    /* ---------- scrolling ---------- */

    onScroll() {
      if (this.dragging || this.momentumRaf) return;
      var self = this;
      window.requestAnimationFrame(function () { self.updateIndex(); });
    }

    updateIndex() {
      if (!this.cards.length) return;
      var trackRect = this.track.getBoundingClientRect();
      var closest = 0;
      var minDist = Infinity;
      this.cards.forEach(function (card, i) {
        var r = card.getBoundingClientRect();
        var dist = Math.abs(r.left - trackRect.left);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      this.index = closest;
      if (this.dots) {
        this.dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === closest);
        });
      }
    }

    scrollToIndex(i) {
      var clamped = Math.max(0, Math.min(this.cards.length - 1, i));
      this.cancelMomentum();
      this.track.scrollTo({ left: this.cards[clamped].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.index = clamped;
    }

    step(dir) {
      var target = this.index + dir;
      var maxIndex = this.cards.length - this.visibleCount;
      if (target > maxIndex) {
        this.scrollToIndex(0);
      } else if (target < 0) {
        this.scrollToIndex(maxIndex);
      } else {
        this.scrollToIndex(target);
      }
    }

    start() {
      var self = this;
      this.stop();
      this.timer = setInterval(function () { self.step(1); }, this.intervalMs);
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    pause() {
      this.stop();
    }

    resume() {
      if (this.autoRotate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.start();
      }
    }
  }
  customElements.define('product-showcase', ProductShowcase);
}
